var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc bar
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Rect, Sector } from '../../element';
import { createRichText } from '../../rich';
import { LegendType } from '../../interface/legend';
import { SeriesModel } from '../../model';
import { isPercent, degToRad, getPercent, getPercentFromStringOrValue } from '../../utils/math';
import { eventGraph } from '../../action';
import { getRectLabelPosition, getSectorLabelPosition } from '../../utils/textLayout';
import { registerChart } from '../index';
import './hook-minlength';
/**
 * default
 */
var BarSeriesModel = /** @class */ (function (_super) {
    __extends(BarSeriesModel, _super);
    function BarSeriesModel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'bar';
        _this.legendType = LegendType.SERIES_LEGEND;
        _this.legendIcon = 'rect';
        _this.stackable = true;
        return _this;
    }
    BarSeriesModel.prototype.getDefaultOption = function () {
        return __assign(__assign({}, _super.prototype.getDefaultOption.call(this)), { type: 'bar', name: '', coord: 'cartesian2d', polar: 0, barGap: '30%', barWidth: null, maxBarWidth: null, minBarLength: 0, backgroundColor: null, backgroundStyle: undefined, grouping: true, groupPadding: '50%', encode: {
                x: '',
                y: '',
                color: '',
            }, xAxis: 0, yAxis: 0, label: {
                show: false,
                position: 'outside',
                rotate: null,
                color: null,
                fontSize: 12,
                distance: 4,
                offsetX: 0,
                offsetY: 0,
                autoHide: true,
            }, shapeStyle: {
                lineWidth: 0,
                stroke: null,
                borderRadius: 0,
                shadowBlur: 0,
                shadowColor: null,
                shadowOffsetX: 0,
                shadowOffsetY: 0,
            }, hover: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            }, selected: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            }, animate: null, visible: true, showLegend: true, selectedMode: false });
    };
    BarSeriesModel.prototype.getInitialState = function () {
        return {
            points: [],
            barWidth: 0,
            needAnimation: this.needAnimation(),
        };
    };
    BarSeriesModel.prototype.getBarItems = function () {
        var _this = this;
        if (!this.visible()) {
            return [];
        }
        var _a = this.option, barWidth = _a.barWidth, barGap = _a.barGap, groupPadding = _a.groupPadding, maxBarWidth = _a.maxBarWidth, grouping = _a.grouping;
        var coordModel = this.chart.getComponentModel('coord');
        var coord = coordModel.getCoordinate(this.option);
        var categoryAxis = coord.getCategoryAxis();
        var valueAxis = coord.getValueAxis();
        var _b = valueAxis.scale, min = _b.min, max = _b.max;
        var _c = categoryAxis.getState(), minCategory = _c.min, maxCategory = _c.max;
        var inverse = valueAxis.getOption().inverse;
        var valueField = this.option.encode[valueAxis.shortType];
        var allBarSeries = categoryAxis
            .getAxisSeries()
            .filter(function (seriesModel) { return seriesModel.type === 'bar'; });
        var visibleBarSeriesModels = allBarSeries.filter(function (series) { return series.visible(); });
        var stackList = [];
        var currentGroupIndex = 0;
        if (grouping) {
            visibleBarSeriesModels.forEach(function (barModel, index) {
                var currentBarOption = barModel.getOption();
                // eslint-disable-next-line prefer-const
                var stack = currentBarOption.stack, _a = currentBarOption.stacking, stacking = _a === void 0 ? 'normal' : _a;
                var isNullStack = lodash.isNull(stack) || lodash.isUndefined(stack);
                if (stacking === 'percent') {
                    stack = "PERCENT_STACKING_" + (stack || 'default-stack');
                }
                else if (isNullStack) {
                    stack = "NORMAL_STACK_" + index;
                }
                if (currentBarOption.grouping && stackList.indexOf(stack) === -1) {
                    stackList.push(stack);
                }
                if (barModel === _this) {
                    currentGroupIndex = stackList.indexOf(stack);
                }
            });
        }
        var groupCount = grouping ? stackList.length : 1;
        var stackDataKey = this.getStackOnDatakey();
        var dataset = this.getDataset();
        var stackData = lodash.isArray(valueField)
            ? this._dataset.pickFieldValues(valueField)
            : dataset.stack(this.getValueField(), stackDataKey);
        var categoryAxisPixelValue = categoryAxis.getPixelValue();
        var percentStackSumList = [];
        var allStackedDataKeys = this.getAllPercentStackDatakey();
        if (this.option.stacking === 'percent') {
            percentStackSumList = this._dataset.sumFields(allStackedDataKeys);
        }
        var groupPaddingValue = isPercent(groupPadding)
            ? parseFloat(groupPadding) / 100
            : groupPadding * categoryAxisPixelValue;
        var groupWidthValue = 1 - groupPaddingValue;
        var barWidthValue;
        if (barWidth) {
            barWidthValue = getPercentFromStringOrValue(barWidth, 1 / categoryAxisPixelValue);
        }
        else {
            barWidthValue =
                groupWidthValue /
                    (groupCount +
                        getPercentFromStringOrValue(barGap, 1 / categoryAxisPixelValue) * (groupCount - 1));
        }
        var barPixelWidth = barWidthValue / categoryAxisPixelValue;
        if (lodash.isNumber(maxBarWidth) && Math.abs(barPixelWidth) > maxBarWidth) {
            barWidthValue = maxBarWidth * categoryAxisPixelValue;
        }
        this.setState({ barWidth: Math.abs(barPixelWidth) });
        var firstBarWidth = visibleBarSeriesModels[0].getState().barWidth;
        var firstBarWidthValue = firstBarWidth * categoryAxisPixelValue;
        var barGapValue = getPercentFromStringOrValue(barGap, firstBarWidthValue / categoryAxisPixelValue) *
            firstBarWidthValue;
        return stackData.map(function (data, dataIndex) {
            var groupCenter = dataIndex;
            var barCenter = groupCount === 1
                ? groupCenter
                : groupCenter +
                    (firstBarWidthValue + barGapValue) * (currentGroupIndex - (groupCount - 1) / 2);
            var shapeRef = createRef();
            var labelRef = createRef();
            var record = dataset.getRecord(dataIndex);
            var selected = _this.selectedItems.has(record);
            var encodeData = _this._encode.getEncodeData(record, dataIndex);
            var color = encodeData.color;
            var startValue = data[0], endValue = data[1];
            if (_this.option.stacking === 'percent') {
                startValue = getPercent(startValue, percentStackSumList[dataIndex], allStackedDataKeys, stackDataKey.length);
                endValue = getPercent(endValue, percentStackSumList[dataIndex], allStackedDataKeys, stackDataKey.length + 1);
            }
            startValue = lodash.clamp(startValue, min, max);
            endValue = lodash.clamp(endValue, min, max);
            var name = encodeData[categoryAxis.shortType];
            var value = encodeData[valueAxis.shortType];
            var filtered = !(dataIndex >= minCategory && dataIndex <= maxCategory);
            var visible = !_this.invisibleItems.has(dataIndex) && !filtered;
            var percent = _this.option.stacking === 'percent'
                ? getPercent(value, percentStackSumList[dataIndex], allStackedDataKeys)
                    .toFixed(2)
                    .replace('.00', '') + "%"
                : undefined;
            if (categoryAxis.parentModel.type === 'grid') {
                // const { left, right, top, bottom } = (categoryAxis.parentModel as Grid).getState();
                var plotStart = coord.valueToCoord(startValue, barCenter);
                var plotEnd = coord.valueToCoord(endValue, barCenter);
                if (categoryAxis.type === 'xAxis') {
                    var midX = plotStart.x;
                    var x = Math.min(midX - barWidthValue / categoryAxisPixelValue / 2, midX + barWidthValue / categoryAxisPixelValue / 2);
                    var y = Math.min(plotStart.y, plotEnd.y);
                    var width = Math.abs(barWidthValue / categoryAxisPixelValue);
                    var height = Math.abs(plotStart.y - plotEnd.y);
                    var direction = plotEnd.y > plotStart.y ? 'bottom' : 'top';
                    if (plotEnd.y === plotStart.y) {
                        direction = inverse ? 'bottom' : 'top';
                    }
                    return {
                        encode: encodeData,
                        name: name,
                        color: color,
                        value: value,
                        percent: percent,
                        plotX: plotEnd.x,
                        plotY: plotEnd.y,
                        visible: visible,
                        direction: direction,
                        selected: selected,
                        filtered: filtered,
                        range: data,
                        shape: {
                            type: 'rect',
                            x: x,
                            y: y,
                            width: width,
                            height: height,
                        },
                        shapeRef: shapeRef,
                        labelRef: labelRef,
                    };
                }
                if (categoryAxis.type === 'yAxis') {
                    var midY = plotStart.y;
                    var x = Math.min(plotStart.x, plotEnd.x);
                    var y = Math.min(midY - barWidthValue / categoryAxisPixelValue / 2, midY + barWidthValue / categoryAxisPixelValue / 2);
                    var width = Math.abs(plotStart.x - plotEnd.x);
                    var height = Math.abs(barWidthValue / categoryAxisPixelValue);
                    var direction = plotEnd.x > plotStart.x ? 'right' : 'left';
                    if (plotEnd.x === plotStart.x) {
                        direction = inverse ? 'left' : 'right';
                    }
                    return {
                        encode: encodeData,
                        range: data,
                        name: name,
                        color: color,
                        value: value,
                        percent: percent,
                        plotX: plotEnd.x,
                        plotY: plotEnd.y,
                        visible: visible,
                        direction: direction,
                        selected: selected,
                        filtered: filtered,
                        shape: {
                            type: 'rect',
                            x: x,
                            y: y,
                            width: width,
                            height: height,
                        },
                        shapeRef: shapeRef,
                    };
                }
            }
            else if (categoryAxis.parentModel.type === 'polar') {
                var _a = coord.valueToCoord(startValue, barCenter), startAngle = _a.angle, startRadius = _a.radius;
                var _b = coord.valueToCoord(endValue, barCenter), endAngle = _b.angle, endRadius = _b.radius;
                var _c = coord.getCenter(), cx = _c[0], cy = _c[1];
                if (categoryAxis.type === 'angleAxis') {
                    // startAngle === endAngle is true
                    var midAngle = startAngle;
                    var midAngleValue = categoryAxis.scale.invert(midAngle);
                    var direction = endRadius >= startRadius ? 'radius' : 'innerRadius';
                    var plotEnd = categoryAxis.parentModel.getCoordinate().coordToPoint({
                        angle: midAngle,
                        radius: endRadius,
                    });
                    return {
                        encode: encodeData,
                        range: data,
                        shape: {
                            type: 'sector',
                            cx: cx,
                            cy: cy,
                            startAngle: categoryAxis.scale.scale(midAngleValue - barWidthValue / 2),
                            endAngle: categoryAxis.scale.scale(midAngleValue + barWidthValue / 2),
                            radius: Math.max(startRadius, endRadius),
                            innerRadius: Math.min(startRadius, endRadius),
                        },
                        name: name,
                        value: value,
                        percent: percent,
                        color: color,
                        plotX: plotEnd.x,
                        plotY: plotEnd.y,
                        direction: direction,
                        visible: visible,
                        selected: selected,
                        filtered: filtered,
                        shapeRef: shapeRef,
                        labelRef: labelRef,
                    };
                }
                if (categoryAxis.type === 'radiusAxis') {
                    // startRadius === endRadius;
                    var midRadius = startRadius;
                    var radiusArr = [
                        midRadius - barWidthValue / categoryAxisPixelValue / 2,
                        midRadius + barWidthValue / categoryAxisPixelValue / 2,
                    ];
                    var direction = endAngle >= startAngle ? 'endAngle' : 'startAngle';
                    var plotEnd = categoryAxis.parentModel.getCoordinate().coordToPoint({
                        angle: endAngle,
                        radius: midRadius,
                    });
                    return {
                        encode: encodeData,
                        range: data,
                        shape: {
                            type: 'sector',
                            cx: cx,
                            cy: cy,
                            startAngle: startAngle,
                            endAngle: endAngle,
                            radius: lodash.max(radiusArr),
                            innerRadius: lodash.min(radiusArr),
                        },
                        name: name,
                        value: value,
                        percent: percent,
                        color: color,
                        plotX: plotEnd.x,
                        plotY: plotEnd.y,
                        direction: direction,
                        visible: true,
                        selected: selected,
                        filtered: filtered,
                        shapeRef: shapeRef,
                    };
                }
            }
        });
    };
    BarSeriesModel.prototype.beforeRender = function () {
        if (this.visible()) {
            this.state.points = this.getBarItems();
        }
    };
    BarSeriesModel.prototype.afterRender = function () {
        this.setState({ needAnimation: false });
    };
    BarSeriesModel.prototype.toggleSelectPoint = function (dataIndex, selected) {
        var _a;
        _super.prototype.toggleSelectPoint.call(this, dataIndex, selected);
        var point = this.state.points[dataIndex];
        (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(__assign(__assign({ fill: point.color, stroke: point.color }, this.getShapeStyle(this.option.shapeStyle)), (selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
    };
    BarSeriesModel.prototype.toggleHighlightPoint = function (dataIndex, active) {
        var _a;
        if (this.option.pattern) {
            return;
        }
        if (this.isLargeData()) {
            return;
        }
        var point = this.state.points[dataIndex];
        (_a = point.shapeRef.current) === null || _a === void 0 ? void 0 : _a.setAttr(__assign(__assign({ fill: active ? this.getHoverColor(point.color) : point.color }, this.getShapeStyle(active ? this.option.hover.shapeStyle : this.option.shapeStyle)), (point.selected ? this.getShapeStyle(this.option.selected.shapeStyle) : null)));
    };
    BarSeriesModel.prototype.renderBarShadow = function () {
        var _this = this;
        var _a = this.option, backgroundColor = _a.backgroundColor, shapeStyle = _a.shapeStyle, backgroundStyle = _a.backgroundStyle;
        var isFnColor = lodash.isFunction(backgroundColor);
        var shadowGroup = new Group({
            key: this.getKey('bar-shadow'),
            fill: !isFnColor ? backgroundColor : undefined,
        });
        var coord = this.getCoord();
        var valueAxis = coord.getValueAxis();
        var _b = valueAxis.scale, end = _b.end, start = _b.start;
        var dataset = this._dataset;
        this.state.points.forEach(function (point, dataIndex) {
            var direction = point.direction, filtered = point.filtered;
            if (filtered) {
                return;
            }
            if (point.shape.type === 'rect') {
                var _a = point.shape, x = _a.x, y = _a.y, width = _a.width, height = _a.height;
                var isHorizontal = ['right', 'left'].indexOf(direction) > -1;
                var maxValue = end - start;
                var barShadow = new Rect(__assign({ key: dataIndex, x: isHorizontal ? start : Math.floor(x) + 0.5, y: isHorizontal ? Math.floor(y) + 0.5 : start, width: _this.visible() && point.visible ? (isHorizontal ? maxValue : width) : 0, height: isHorizontal ? height : maxValue, r: shapeStyle.borderRadius, fill: isFnColor
                        ? backgroundColor(point.color, dataset.getRecord(dataIndex))
                        : undefined, pointerEvents: 'none' }, backgroundStyle));
                shadowGroup.add(barShadow);
            }
            else if (point.shape.type === 'sector') {
                var _b = point.shape, cx = _b.cx, cy = _b.cy, startAngle = _b.startAngle, endAngle = _b.endAngle, innerRadius = _b.innerRadius, radius = _b.radius;
                var isRadius = ['radius', 'innerRadius'].indexOf(direction) > -1;
                var barShadow = new Sector({
                    key: dataIndex,
                    cx: cx,
                    cy: cy,
                    radius: isRadius ? end : radius,
                    radiusI: isRadius ? start : innerRadius,
                    start: degToRad(isRadius ? startAngle : start),
                    end: _this.visible() && point.visible
                        ? degToRad(isRadius ? endAngle : end)
                        : degToRad(isRadius ? startAngle : start),
                    pointerEvents: 'none',
                });
                shadowGroup.add(barShadow);
            }
        });
        return shadowGroup;
    };
    BarSeriesModel.prototype.renderBarItems = function () {
        var _this = this;
        var _a = this.option.shapeStyle, fill = _a.fill, stroke = _a.stroke, borderRadius = _a.borderRadius;
        var fillColor = fill || this.getSeriesColor(true);
        var strokeColor = stroke || this.getSeriesColor();
        var colorEncode = this.option.encode.color;
        var group = new Group(__assign(__assign({ key: this.getKey('bar-item'), fill: fillColor, stroke: strokeColor }, this.getShapeStyle(this.option.shapeStyle)), { onEvent: function (event) { return _this.handleSeriesUIEvent(event); } }));
        var coord = this.getCoord();
        var categoryAxis = coord.getCategoryAxis();
        var barItemList = [];
        var needAnimation = this.needAnimation();
        var transitionProperty = this.getTransitionProperty();
        this.state.points.forEach(function (point, dataIndex) {
            var direction = point.direction, filtered = point.filtered, selected = point.selected;
            if (filtered) {
                return;
            }
            var onEvent = function (event) {
                return _this.handleUIEvent(event, 'point', {
                    title: point.encode[categoryAxis.shortType],
                    point: _this.getWrappedPoint(dataIndex),
                    hasAxis: true,
                });
            };
            var itemStyle = colorEncode
                ? _this.getShapeStyle({
                    fill: fill || point.color,
                    stroke: stroke || point.color,
                })
                : null;
            if (point.shape.type === 'rect') {
                var _a = point.shape, x_1 = _a.x, y_1 = _a.y, width_1 = _a.width, height_1 = _a.height;
                var barItem_1 = new Rect(__assign(__assign(__assign({ key: dataIndex, ref: point.shapeRef, x: Math.floor(x_1) + 0.5, y: Math.floor(y_1) + 0.5, width: _this.visible() && point.visible ? width_1 : 0, height: height_1, cursor: 'pointer', opacity: _this.visible() && point.visible ? 1 : 0, transitionProperty: transitionProperty, r: borderRadius }, itemStyle), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)), { onEvent: onEvent, onMounted: needAnimation
                        ? function () {
                            var animateAttr = { x: x_1, y: y_1, width: width_1, height: height_1 };
                            if (direction === 'right') {
                                animateAttr.width = 0;
                            }
                            else if (direction === 'left') {
                                animateAttr.x += width_1;
                                animateAttr.width = 0;
                            }
                            else if (direction === 'top') {
                                animateAttr.y += height_1;
                                animateAttr.height = 0;
                            }
                            else if (direction === 'bottom') {
                                animateAttr.height = 0;
                            }
                            barItem_1.setAttr(animateAttr).animateTo({
                                x: x_1,
                                y: y_1,
                                width: width_1,
                                height: height_1,
                            }, 1000);
                        }
                        : null }));
                barItemList.push(barItem_1);
            }
            else if (point.shape.type === 'sector') {
                var _b = point.shape, cx = _b.cx, cy = _b.cy;
                var _c = point.shape, startAngle = _c.startAngle, innerRadius_1 = _c.innerRadius;
                var _d = point.shape, endAngle = _d.endAngle, radius_1 = _d.radius;
                var start_1 = degToRad(startAngle);
                var end_1 = _this.visible() && point.visible ? degToRad(endAngle) : start_1;
                var barItem_2 = new Sector(__assign(__assign(__assign({ key: dataIndex, ref: point.shapeRef, cx: cx,
                    cy: cy,
                    radius: radius_1, radiusI: innerRadius_1, start: start_1,
                    end: end_1, cursor: 'pointer', opacity: _this.visible() && point.visible ? 1 : 0, transitionProperty: transitionProperty, round: _this.option.shapeStyle.round }, itemStyle), (selected ? _this.getShapeStyle(_this.option.selected.shapeStyle) : null)), { onEvent: onEvent, onMounted: function () {
                        if (!needAnimation) {
                            return;
                        }
                        var animateAttr = { start: start_1, end: end_1, radius: radius_1 };
                        if (direction === 'startAngle' || direction === 'endAngle') {
                            animateAttr.end = start_1;
                        }
                        else {
                            animateAttr.radius = innerRadius_1;
                        }
                        barItem_2.setAttr(animateAttr).animateTo({ start: start_1, end: end_1, radius: radius_1 });
                        // todo
                    } }));
                barItemList.push(barItem_2);
            }
        });
        if (barItemList.length < this.option.chunkLimit) {
            group.addAll(barItemList);
        }
        else {
            group.replaceChunks(this.getChunks(barItemList));
        }
        return group;
    };
    BarSeriesModel.prototype.getLabelPosition = function (point, animate) {
        var shape = point.shape, direction = point.direction;
        var _a = shape, x = _a.x, y = _a.y, width = _a.width, height = _a.height;
        var _b = this.option.label, position = _b.position, distance = _b.distance;
        if (animate) {
            if (direction === 'right') {
                width = 0;
            }
            else if (direction === 'left') {
                x += width;
                width = 0;
            }
            else if (direction === 'top') {
                y += height;
                height = 0;
            }
            else if (direction === 'bottom') {
                height = 0;
            }
        }
        return getRectLabelPosition({
            top: y,
            left: x,
            right: x + width,
            bottom: y + height,
        }, position, distance, direction);
    };
    BarSeriesModel.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('bar-label'),
        });
        var points = this.state.points;
        var _a = this.option.label, show = _a.show, rich = _a.rich, position = _a.position, distance = _a.distance, offsetY = _a.offsetY, offsetX = _a.offsetX, autoHide = _a.autoHide;
        var inside = position.indexOf('inside') !== -1;
        var transitionProperty = this.getTransitionProperty();
        var needAnimation = this.state.needAnimation;
        points.forEach(function (point, dataIndex) {
            var color = point.color, visible = point.visible, shape = point.shape, direction = point.direction, filtered = point.filtered;
            if (filtered) {
                return;
            }
            var textPosition;
            if (shape.type === 'rect') {
                textPosition = _this.getLabelPosition(point, needAnimation);
            }
            else if (shape.type === 'sector') {
                var cx = shape.cx, cy = shape.cy, startAngle = shape.startAngle, endAngle = shape.endAngle, radius = shape.radius, innerRadius = shape.innerRadius;
                textPosition = getSectorLabelPosition({
                    cx: cx,
                    cy: cy,
                    startAngle: startAngle,
                    endAngle: endAngle,
                    radius: radius,
                    innerRadius: innerRadius,
                }, position, distance, direction);
            }
            textPosition.x += offsetX;
            textPosition.y += offsetY;
            var label = createRichText(__assign(__assign(__assign(__assign({ key: dataIndex, ref: point.labelRef, display: show && _this.visible() && visible, rich: _this.option.label.rich, text: _this.getDataLabel(dataIndex) }, textPosition), { fill: inside ? '#ffffff' : '#6E748E', stroke: inside ? color : '#ffffff', lineWidth: 2 }), _this.getTextStyle(_this.option.label)), { pointerEvents: inside ? 'none' : 'auto', transitionProperty: transitionProperty, onEvent: function (event) {
                    return _this.handleUIEvent(event, eventGraph.LABEL, {
                        point: _this.getWrappedPoint(dataIndex),
                    });
                }, onMounted: function () {
                    if (needAnimation && shape.type === 'rect') {
                        var nextPosition = _this.getLabelPosition(point, false);
                        nextPosition.x += offsetX;
                        nextPosition.y += offsetY;
                        label.stopAllAnimation().animateTo(nextPosition, 1000);
                    }
                } }));
            group.add(label);
        });
        autoHide && !rich && this.chart.__autoHideLabel(group.childNodes);
        return group;
    };
    BarSeriesModel.prototype.render = function () {
        var group = new Group({
            key: this.getKey('bar'),
        });
        if (this.option.backgroundColor && this.getStackOnSeries().length === 0) {
            group.add(this.renderBarShadow());
        }
        group.add(this.renderBarItems());
        if (this.option.label.show) {
            this.chart.addToTopLevel(this.renderLabels());
        }
        return group;
    };
    BarSeriesModel.dependencies = ['coord', 'legend'];
    return BarSeriesModel;
}(SeriesModel));
export default BarSeriesModel;
registerChart('bar', BarSeriesModel);
